import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type ReminderActionType = 'call' | 'email' | 'meeting' | 'sms' | 'task' | 'quote' | 'video_call' | 'note';

export const REMINDER_ACTION_TYPES: { value: ReminderActionType; label: string; icon: string }[] = [
  { value: 'call', label: 'Klic', icon: 'Phone' },
  { value: 'email', label: 'Email', icon: 'Mail' },
  { value: 'meeting', label: 'Sestanek', icon: 'Users' },
  { value: 'video_call', label: 'Video klic', icon: 'Video' },
  { value: 'sms', label: 'SMS', icon: 'MessageSquare' },
  { value: 'task', label: 'Naloga', icon: 'CheckSquare' },
  { value: 'quote', label: 'Ponudba', icon: 'FileText' },
  { value: 'note', label: 'Zapisek', icon: 'StickyNote' },
];

export interface Reminder {
  id: string;
  contact_submission_id: string;
  user_id: string;
  due_date: string;
  description: string | null;
  action_type: ReminderActionType | null;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReminderWithContact extends Reminder {
  contact_submissions?: {
    id: string;
    name: string;
    company: string;
    email: string;
    status: string;
  };
}

export function useReminders(contactSubmissionId?: string) {
  const { toast } = useToast();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchReminders = async () => {
    setIsLoading(true);
    let query = supabase
      .from("crm_reminders")
      .select("*")
      .order("due_date", { ascending: true });

    if (contactSubmissionId) {
      query = query.eq("contact_submission_id", contactSubmissionId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching reminders:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri nalaganju opomnikov",
        variant: "destructive",
      });
    } else {
      setReminders((data || []) as Reminder[]);
    }
    setIsLoading(false);
  };

  const createReminder = async (
    contactSubmissionId: string,
    dueDate: Date,
    description?: string,
    actionType?: ReminderActionType
  ) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      toast({
        title: "Napaka",
        description: "Niste prijavljeni",
        variant: "destructive",
      });
      return null;
    }

    const { data, error } = await supabase
      .from("crm_reminders")
      .insert({
        contact_submission_id: contactSubmissionId,
        user_id: user.id,
        due_date: dueDate.toISOString(),
        description: description || null,
        action_type: actionType || 'task',
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating reminder:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri ustvarjanju opomnika",
        variant: "destructive",
      });
      return null;
    }

    toast({
      title: "Uspešno",
      description: "Opomnik ustvarjen",
    });
    await fetchReminders();
    return data;
  };

  const completeReminder = async (reminderId: string) => {
    const { error } = await supabase
      .from("crm_reminders")
      .update({
        is_completed: true,
        completed_at: new Date().toISOString(),
      })
      .eq("id", reminderId);

    if (error) {
      console.error("Error completing reminder:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri zaključevanju opomnika",
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: "Uspešno",
      description: "Opomnik označen kot opravljen",
    });
    await fetchReminders();
    return true;
  };

  const uncompleteReminder = async (reminderId: string) => {
    const { error } = await supabase
      .from("crm_reminders")
      .update({
        is_completed: false,
        completed_at: null,
      })
      .eq("id", reminderId);

    if (error) {
      console.error("Error uncompleting reminder:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri ponovnem odpiranju opomnika",
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: "Uspešno",
      description: "Opomnik ponovno odprt",
    });
    await fetchReminders();
    return true;
  };

  const deleteReminder = async (reminderId: string) => {
    const { error } = await supabase
      .from("crm_reminders")
      .delete()
      .eq("id", reminderId);

    if (error) {
      console.error("Error deleting reminder:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri brisanju opomnika",
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: "Uspešno",
      description: "Opomnik izbrisan",
    });
    await fetchReminders();
    return true;
  };

  const updateReminder = async (
    reminderId: string,
    updates: {
      due_date?: Date;
      description?: string;
      action_type?: ReminderActionType;
    }
  ) => {
    const updateData: Record<string, unknown> = {};
    if (updates.due_date) {
      updateData.due_date = updates.due_date.toISOString();
    }
    if (updates.description !== undefined) {
      updateData.description = updates.description || null;
    }
    if (updates.action_type) {
      updateData.action_type = updates.action_type;
    }

    const { error } = await supabase
      .from("crm_reminders")
      .update(updateData)
      .eq("id", reminderId);

    if (error) {
      console.error("Error updating reminder:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri posodabljanju opomnika",
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: "Uspešno",
      description: "Opomnik posodobljen",
    });
    await fetchReminders();
    return true;
  };

  useEffect(() => {
    fetchReminders();
  }, [contactSubmissionId]);

  const activeReminders = reminders.filter((r) => !r.is_completed);
  const completedReminders = reminders.filter((r) => r.is_completed);

  return {
    reminders,
    activeReminders,
    completedReminders,
    isLoading,
    fetchReminders,
    createReminder,
    updateReminder,
    completeReminder,
    uncompleteReminder,
    deleteReminder,
  };
}

export function useAllRemindersWithContacts() {
  const { toast } = useToast();
  const [reminders, setReminders] = useState<ReminderWithContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchReminders = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("crm_reminders")
      .select(`
        *,
        contact_submissions (
          id,
          name,
          company,
          email,
          status
        )
      `)
      .order("due_date", { ascending: true });

    if (error) {
      console.error("Error fetching reminders with contacts:", error);
      toast({
        title: "Napaka",
        description: "Napaka pri nalaganju opomnikov",
        variant: "destructive",
      });
    } else {
      setReminders((data || []) as ReminderWithContact[]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const activeReminders = reminders.filter((r) => !r.is_completed);
  const completedReminders = reminders.filter((r) => r.is_completed);

  return {
    reminders,
    activeReminders,
    completedReminders,
    isLoading,
    fetchReminders,
  };
}

// Blocked time types for booking form
export const BLOCKED_ACTION_TYPES: ReminderActionType[] = ['video_call', 'meeting', 'call'];

// Hook to get occupied time slots for blocking in booking form
export function useOccupiedTimeSlots() {
  const [occupiedSlots, setOccupiedSlots] = useState<{ date: string; hour: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOccupiedSlots = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.rpc("get_occupied_consultation_slots");

    if (error) {
      console.error("Error fetching occupied slots:", error);
    } else {
      const slots = (data || []).map((r: { due_date: string; action_type: string }) => {
        const date = new Date(r.due_date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return {
          date: `${year}-${month}-${day}`,
          hour: date.getHours(),
        };
      });
      setOccupiedSlots(slots);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOccupiedSlots();
  }, []);

  const isSlotOccupied = (selectedDate: Date, hour: number): boolean => {
    // Format selected date as YYYY-MM-DD in local timezone
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return occupiedSlots.some((slot) => slot.date === dateStr && slot.hour === hour);
  };

  return { occupiedSlots, isLoading, fetchOccupiedSlots, isSlotOccupied };
}

export function useReminderCounts() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchCounts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("crm_reminders")
      .select("contact_submission_id")
      .eq("is_completed", false);

    if (error) {
      console.error("Error fetching reminder counts:", error);
    } else {
      const countMap: Record<string, number> = {};
      data?.forEach((r) => {
        countMap[r.contact_submission_id] = (countMap[r.contact_submission_id] || 0) + 1;
      });
      setCounts(countMap);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  return { counts, isLoading, fetchCounts };
}
